import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'

import { cn } from '@/lib/utils'
import { ArrowRight } from 'lucide-react'

type StatTone = 'default' | 'success' | 'warning' | 'danger' | 'in' | 'out'

interface StatTileProps {
    label: string
    value: ReactNode
    hint?: ReactNode
    icon: LucideIcon
    tone?: StatTone
    href?: string
    hrefLabel?: string
    className?: string
}

const ICON_STYLES: Record<StatTone, string> = {
    default: 'bg-accent text-accent-foreground',
    success: 'bg-success-soft text-success-ink',
    warning: 'bg-warning-soft text-warning-ink',
    danger: 'bg-danger-soft text-danger-ink',
    in: 'bg-flow-in-soft text-flow-in-ink',
    out: 'bg-flow-out-soft text-flow-out-ink'
}

const StatTile: FC<StatTileProps> = ({
    label,
    value,
    hint,
    icon: Icon,
    tone = 'default',
    href,
    hrefLabel,
    className
}): ReactNode => {
    const content = (
        <>
            <div className='flex items-start justify-between gap-3'>
                <p className='text-sm font-medium text-muted-foreground'>
                    {label}
                </p>

                <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg', ICON_STYLES[tone])}>
                    <Icon className='size-4' aria-hidden='true' />
                </span>
            </div>

            <p className='mt-1 font-heading text-2xl font-semibold tracking-tight tabular-nums sm:text-[1.65rem]'>
                {value}
            </p>

            {hint && (
                <div className='mt-1.5 text-xs text-muted-foreground'>
                    {hint}
                </div>
            )}

            {href && hrefLabel && (
                <span className='mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary'>
                    {hrefLabel}
                    <ArrowRight className='size-3.5 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                </span>
            )}
        </>
    )

    const base = cn('group flex min-w-0 flex-col rounded-xl border bg-card p-4 shadow-xs sm:p-5', className)

    if (href) {
        return (
            <Link
                href={href}
                className={cn(base, 'transition-colors hover:border-primary/40 hover:bg-accent/30 focus-visible:ring-3 focus-visible:ring-ring/50 outline-none')}
            >
                {content}
            </Link>
        )
    }

    return <div className={base}>{content}</div>
}

export default StatTile
